import React from "react";
import {
  AppBar as MuiAppBar,
  Chip,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
} from "@mui/material";
import { ArrowCircleLeftSharp, Menu as MenuIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import "../../css/appbar.css";
import logoExpo from "../../images/logo.png";
import { useRouterStore } from "../../../libs/stores";
import { useWidth } from "../../../libs/hooks";
import { useNavigate } from "react-router-dom";
import { UserAvatar, SriSyncNotificationBell} from "../../../libs/shared/components";

export const drawerWidth = 180;

const propsTooltip = {
  tooltip: {
    sx: {
      background: "#1D9E75",
      color: "#ffffff",
      fontSize: "11px",
      fontWeight: 600,
      borderRadius: "6px",
      px: 1.2,
      py: 0.5,
    },
  },
  arrow: {
    sx: { color: "#1D9E75" },
  },
};

// ── Styled AppBar ─────────────────────────────────────────────────────────────
const AppBarElement = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "small",
})<{ open?: boolean; small?: string }>(({ theme, open, small }) => ({
  // Reserva el alto de la barra de estado / notch del iPhone (PWA standalone).
  // En navegador o Android el env() vale 0px y no cambia nada.
  paddingTop: "env(safe-area-inset-top, 0px)",
  height: "calc(57px + env(safe-area-inset-top, 0px))",
  minHeight: "calc(57px + env(safe-area-inset-top, 0px))",
  maxHeight: "calc(57px + env(safe-area-inset-top, 0px))",
  zIndex: 1001,
  background: "linear-gradient(135deg, #2e2868 0%, #1e1948 100%)",
  boxShadow: "0 2px 16px rgba(83, 74, 183, 0.35)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: small === "true" ? 0 : drawerWidth,
    width: small === "true" ? "100%" : `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// ── AppBar ────────────────────────────────────────────────────────────────────
export const AppBar = ({
  authTokens,
  handleDrawerOpen,
  open,
  clientId,
  branchId,
  role,
}: {
  authTokens?: any;
  handleDrawerOpen: () => void;
  open: boolean;
  // NUEVO: mismos datos que ya le pasás a useStockSocket en algún lado —
  // se necesitan acá para que la campanita se una al room correcto de
  // Socket.io. Si tu app ya los resuelve desde un store (ej. useUserStore),
  // podés sacarlos de ahí adentro del componente en vez de recibirlos por
  // prop — lo dejé como prop porque no vi cómo accedés a esos datos hoy.
  clientId?: any;
  branchId?: string | null;
  role?: string;
}) => {
  const width = useWidth();
  const navigate = useNavigate();
  const isSmall = width === "xs";

  return (
    <React.Fragment>
      <AppBarElement
        position="fixed"
        open={open}
        small={isSmall ? "true" : "false"}
      >
        <Toolbar className="toolbar">
          {/* ── Izquierda: menú + volver ── */}
          <div className="appbar-left">
            {/* Botón hamburguesa — oculto cuando drawer está abierto en desktop */}
            {(!open || isSmall) && (
              <Tooltip
                title="Abrir menú"
                placement="bottom"
                arrow
                componentsProps={propsTooltip}
              >
                <IconButton
                  className="appbar-menu-btn"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                >
                  <MenuIcon style={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Botón volver con Tooltip */}
            <Tooltip
              title="Página anterior"
              placement="right"
              arrow
              componentsProps={propsTooltip}
            >
              <Button
                className="appbar-back-btn"
                onClick={() => navigate(-1)}
                size="small"
              >
                <ArrowCircleLeftSharp style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          </div>

          {/* ── Centro: logo + separador + chip sección ── */}
          <div className="appbar-center">
            <img src={logoExpo} alt="Stockify" className="logo" />
            <ElChip />
          </div>

          {/* ── Derecha: campanita + avatar ── */}
          <div className="appbar-right">
            {clientId && (
              <SriSyncNotificationBell
                clientId={clientId}
                branchId={branchId ?? null}
                role={role ?? ""}
                onClickNotificacion={(n) =>
                  navigate(
                    n.tipo === "factura" && n.referenciaId
                      ? `/stockify/sales/show/${n.referenciaId}`
                      : "/stockify/sri/tareas",
                  )
                }
              />
            )}
            <div className="avatar-contain">
              <UserAvatar />
            </div>
          </div>
        </Toolbar>
      </AppBarElement>
    </React.Fragment>
  );
};

// ── Chip de sección activa ────────────────────────────────────────────────────
export const ElChip = () => {
  const section = useRouterStore((state) => state.section);
  if (!section) return null;

  return (
    <>
      <div className="appbar-separator" />
      <Chip
        className="appbar-chip"
        title={section}
        color="secondary"
        size="medium"
        label={section}
      />
    </>
  );
};